import _classPrivateFieldLooseBase from "@babel/runtime/helpers/esm/classPrivateFieldLooseBase";
import _classPrivateFieldLooseKey from "@babel/runtime/helpers/esm/classPrivateFieldLooseKey";
// Enhanced metrics this.tracker for React compiler performance tracking

import { logMetric } from './debug';
var _renderCount = /*#__PURE__*/_classPrivateFieldLooseKey("renderCount");
var _avgRenderTime = /*#__PURE__*/_classPrivateFieldLooseKey("avgRenderTime");
var _renderReason = /*#__PURE__*/_classPrivateFieldLooseKey("renderReason");
export class ReactCompilerMetrics {
  constructor(tracker) {
    this.tracker = undefined;
    this.isCompilerOptimized = false;
    Object.defineProperty(this, _renderCount, {
      writable: true,
      value: 0
    });
    Object.defineProperty(this, _avgRenderTime, {
      writable: true,
      value: 0
    });
    Object.defineProperty(this, _renderReason, {
      writable: true,
      value: 'unknown'
    });
    this.tracker = tracker;
  }
  getRenderCount() {
    return _classPrivateFieldLooseBase(this, _renderCount)[_renderCount];
  }
  getAvgRenderTime() {
    return _classPrivateFieldLooseBase(this, _avgRenderTime)[_avgRenderTime];
  }
  getRenderReason() {
    return _classPrivateFieldLooseBase(this, _renderReason)[_renderReason];
  }
  setRenderReason(renderReason) {
    _classPrivateFieldLooseBase(this, _renderReason)[_renderReason] = renderReason;
  }
  setRenderCount(renderCount) {
    _classPrivateFieldLooseBase(this, _renderCount)[_renderCount] = renderCount;
  }
  setAvgRenderTime(avgRenderTime) {
    _classPrivateFieldLooseBase(this, _avgRenderTime)[_avgRenderTime] = avgRenderTime;
  }
  /**
   * Set the global compiler optimized state
   */
  setCompilerOptimized(isOptimized) {
    this.isCompilerOptimized = isOptimized;
  }

  /**
   * Track component render performance with React compiler context
   */
  trackComponentRender(options, metrics) {
    const {
      componentName,
      routeName,
      propsCount,
      stateCount,
      childrenCount
    } = options;
    const {
      renderDuration,
      isReactCompilerOptimized,
      renderReason,
      memoryUsage
    } = metrics;
    if (!this.tracker) {
      return;
    }

    // Track render duration by component and compiler state
    this.tracker.timer('component-render-duration', {
      component: componentName,
      route: routeName || 'unknown',
      compiler_optimized: isReactCompilerOptimized.toString(),
      render_reason: renderReason
    }).update(renderDuration);
    logMetric('component-render-duration', {
      component: componentName,
      route: routeName || 'unknown',
      compiler_optimized: isReactCompilerOptimized.toString(),
      render_reason: renderReason
    });

    // Track component complexity metrics
    if (propsCount !== undefined) {
      this.tracker.histogram('component-props-count', {
        component: componentName,
        compiler_optimized: isReactCompilerOptimized.toString()
      }).update(propsCount);
    }
    if (stateCount !== undefined) {
      this.tracker.histogram('component-state-count', {
        component: componentName,
        compiler_optimized: isReactCompilerOptimized.toString()
      }).update(stateCount);
    }
    if (childrenCount !== undefined) {
      this.tracker.histogram('component-children-count', {
        component: componentName,
        compiler_optimized: isReactCompilerOptimized.toString()
      }).update(childrenCount);
    }

    // Track memory usage if available
    if (memoryUsage !== undefined) {
      this.tracker.histogram('component-memory-usage', {
        component: componentName,
        compiler_optimized: isReactCompilerOptimized.toString()
      }).update(memoryUsage);
    }

    // Count total renders by compiler optimization status
    this.tracker.counter('component-renders-total', {
      component: componentName,
      route: routeName || 'unknown',
      compiler_optimized: isReactCompilerOptimized.toString(),
      render_reason: renderReason
    }).increment();
  }

  /**
   * Track re-render prevention by React compiler
   */
  trackRerenderPrevention(componentName, prevented, reason) {
    this.tracker.counter('rerender-prevention-total', {
      component: componentName,
      prevented: prevented.toString(),
      reason
    }).increment();
    logMetric('rerender-prevention-total', {
      component: componentName,
      prevented: prevented.toString(),
      reason
    });
  }

  /**
   * Track bundle size impact of React compiler
   */
  trackBundleMetrics(bundleSize, gzipSize, chunkCount) {
    this.tracker.histogram('bundle-size-bytes', {
      compiler_optimized: this.isCompilerOptimized.toString()
    }).update(bundleSize);
    this.tracker.histogram('bundle-gzip-size-bytes', {
      compiler_optimized: this.isCompilerOptimized.toString()
    }).update(gzipSize);
    this.tracker.histogram('bundle-chunk-count', {
      compiler_optimized: this.isCompilerOptimized.toString()
    }).update(chunkCount);
  }

  /**
   * Track user interaction response time
   */
  trackInteractionResponseTime(interactionType, responseTime, componentName) {
    this.tracker.timer('interaction-response-time', {
      interaction_type: interactionType,
      component: componentName || 'unknown',
      compiler_optimized: this.isCompilerOptimized.toString()
    }).update(responseTime);
    logMetric('interaction-response-time', {
      interaction_type: interactionType,
      component: componentName || 'unknown',
      compiler_optimized: this.isCompilerOptimized.toString()
    });
  }

  /**
   * Track React compiler optimization hits/misses
   */
  trackCompilerOptimization(componentName, optimized, optimizationType) {
    this.tracker.counter('compiler-optimizations-total', {
      component: componentName,
      optimization_type: optimizationType,
      optimized: optimized.toString()
    }).increment();
    logMetric('compiler-optimizations-total', {
      component: componentName,
      optimization_type: optimizationType,
      optimized: optimized.toString()
    });
  }

  /**
   * Track performance comparison between compiler enabled/disabled states
   */
  trackPerformanceComparison(data) {
    const {
      component,
      beforeCompiler,
      afterCompiler,
      improvementPercentage
    } = data;

    // Track improvement percentage
    this.tracker.histogram('performance-improvement-percentage', {
      component,
      metric_type: 'render_time'
    }).update(improvementPercentage);
    logMetric('performance-improvement-percentage', {
      component,
      metric_type: 'render_time',
      improvement_percentage: improvementPercentage
    });

    // Track before/after metrics
    this.tracker.histogram('performance-comparison-render-time', {
      component,
      compiler_state: 'before'
    }).update(beforeCompiler.avgRenderTime);
    logMetric('performance-comparison-render-time', {
      component,
      compiler_state: 'before',
      improvement_percentage: improvementPercentage
    });
    this.tracker.histogram('performance-comparison-render-time', {
      component,
      compiler_state: 'after'
    }).update(afterCompiler.avgRenderTime);
    logMetric('performance-comparison-render-time', {
      component,
      compiler_state: 'after',
      improvement_percentage: improvementPercentage
    });
    this.tracker.histogram('performance-comparison-rerender-count', {
      component,
      compiler_state: 'before'
    }).update(beforeCompiler.rerenderCount);
    logMetric('performance-comparison-rerender-count', {
      component,
      compiler_state: 'before',
      improvement_percentage: improvementPercentage
    });
    this.tracker.histogram('performance-comparison-rerender-count', {
      component,
      compiler_state: 'after'
    }).update(afterCompiler.rerenderCount);
    logMetric('performance-comparison-rerender-count', {
      component,
      compiler_state: 'after',
      improvement_percentage: improvementPercentage
    });
  }

  /**
   * Track React DevTools Profiler data
   */
  trackProfilerData(componentName, phase, actualDuration, baseDuration, startTime, commitTime) {
    this.tracker.histogram('profiler-actual-duration', {
      component: componentName,
      phase,
      compiler_optimized: this.isCompilerOptimized.toString()
    }).update(actualDuration);
    this.tracker.histogram('profiler-base-duration', {
      component: componentName,
      phase,
      compiler_optimized: this.isCompilerOptimized.toString()
    }).update(baseDuration);
    this.tracker.timer('profiler-start-time', {
      component: componentName,
      phase,
      compiler_optimized: this.isCompilerOptimized.toString()
    }).update(startTime);
    this.tracker.timer('profiler-commit-time', {
      component: componentName,
      phase,
      compiler_optimized: this.isCompilerOptimized.toString()
    }).update(commitTime);

    // Calculate efficiency ratio (actual vs base duration)
    const efficiency = baseDuration > 0 ? (baseDuration - actualDuration) / baseDuration : 0;
    this.tracker.histogram('profiler-efficiency-ratio', {
      component: componentName,
      phase,
      compiler_optimized: this.isCompilerOptimized.toString()
    }).update(efficiency);
  }

  /**
   * Track memory leaks and cleanup efficiency
   */
  trackMemoryCleanup(componentName, memoryFreed, cleanupTime) {
    this.tracker.histogram('memory-cleanup-bytes', {
      component: componentName,
      compiler_optimized: this.isCompilerOptimized.toString()
    }).update(memoryFreed);
    this.tracker.timer('memory-cleanup-duration', {
      component: componentName,
      compiler_optimized: this.isCompilerOptimized.toString()
    }).update(cleanupTime);
  }

  /**
   * Track React Suspense and concurrent features performance
   */
  trackSuspenseMetrics(componentName, suspenseTime, fallbackShown, errorBoundaryTriggered) {
    this.tracker.timer('suspense-duration', {
      component: componentName,
      compiler_optimized: this.isCompilerOptimized.toString(),
      fallback_shown: fallbackShown.toString()
    }).update(suspenseTime);
    if (errorBoundaryTriggered) {
      this.tracker.counter('suspense-errors-total', {
        component: componentName,
        compiler_optimized: this.isCompilerOptimized.toString()
      }).increment();
    }
  }

  /**
   * Track component load time from navigation start to mount
   */
  trackComponentLoadTime(componentName, timeToMount, mountDuration, routeName) {
    this.tracker.timer('component-time-to-mount', {
      component: componentName,
      route: routeName || 'unknown'
    }).update(timeToMount);
    logMetric('component-time-to-mount', {
      component: componentName,
      route: routeName || 'unknown',
      time_to_mount: timeToMount
    });
    this.tracker.timer('component-mount-duration', {
      component: componentName,
      route: routeName || 'unknown'
    }).update(mountDuration);
    logMetric('component-mount-duration', {
      component: componentName,
      route: routeName || 'unknown',
      mount_duration: mountDuration
    });
  }

  /**
   * Track component ready time (when data is loaded and component is interactive)
   */
  trackComponentReadyTime(componentName, timeToReady, routeName) {
    this.tracker.timer('component-time-to-ready', {
      component: componentName,
      route: routeName || 'unknown'
    }).update(timeToReady);
    logMetric('component-time-to-ready', {
      component: componentName,
      route: routeName || 'unknown',
      time_to_ready: timeToReady
    });
  }

  /**
   * Track scripts that blocked the main thread during page load
   */
  trackBlockingScript(sourceURL, sourceFunctionName, duration, context) {
    const urlPath = this.extractScriptPath(sourceURL);
    this.tracker.timer('blocking-script-duration', {
      script_path: urlPath,
      function_name: sourceFunctionName,
      context: context || 'unknown'
    }).update(duration);
    logMetric('blocking-script-duration', {
      script_path: urlPath,
      function_name: sourceFunctionName,
      context: context || 'unknown',
      duration
    });
    this.tracker.counter('blocking-scripts-total', {
      script_path: urlPath,
      context: context || 'unknown'
    }).increment();
  }

  /**
   * Track import timing for lazy-loaded modules
   */
  trackImportTiming(moduleName, duration) {
    this.tracker.timer('import-duration', {
      module: moduleName
    }).update(duration);
    logMetric('import-duration', {
      module: moduleName,
      duration
    });
  }
  extractScriptPath(sourceURL) {
    if (!sourceURL || sourceURL === 'unknown') {
      return 'unknown';
    }
    try {
      const url = new URL(sourceURL, window.location.origin);
      return url.pathname;
    } catch (_unused) {
      const match = sourceURL.match(/\/[^?#]+/);
      return match ? match[0] : sourceURL;
    }
  }

  /**
   * Track environment information (non-PII): CPU cores, memory, and network characteristics.
   */
  trackEnvironmentInfo() {
    // CPU cores (logical processors)
    try {
      const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : undefined;
      if (typeof cores === 'number' && isFinite(cores)) {
        this.tracker.histogram('env-hardware-concurrency').update(cores);
        logMetric('env-hardware-concurrency', {
          cores
        });
      }
    } catch (_unused2) {
      // no-op
    }

    // Approximate device memory in GB (Chrome/Edge)
    try {
      const deviceMemory = typeof navigator !== 'undefined' ? navigator.deviceMemory : undefined;
      if (typeof deviceMemory === 'number' && isFinite(deviceMemory)) {
        this.tracker.histogram('env-device-memory-gb').update(deviceMemory);
        logMetric('env-device-memory-gb', {
          deviceMemory
        });
      }
    } catch (_unused3) {
      // no-op
    }

    // Network Information API (effectiveType, downlink, rtt, saveData)
    try {
      const nav = typeof navigator !== 'undefined' ? navigator : undefined;
      const connection = nav && (nav.connection || nav.mozConnection || nav.webkitConnection);
      if (connection) {
        const effectiveType = typeof connection.effectiveType === 'string' ? connection.effectiveType : undefined;
        const downlink = typeof connection.downlink === 'number' ? connection.downlink : undefined; // Mbps
        const rtt = typeof connection.rtt === 'number' ? connection.rtt : undefined; // ms
        const saveData = typeof connection.saveData === 'boolean' ? connection.saveData : undefined;
        if (effectiveType) {
          this.tracker.counter('env-network-type-total', {
            effective_type: effectiveType
          }).increment();
          logMetric('env-network-type-total', {
            effective_type: effectiveType
          });
        }
        if (typeof downlink === 'number' && isFinite(downlink)) {
          this.tracker.histogram('env-network-downlink-mbps', {
            effective_type: effectiveType || 'unknown'
          }).update(downlink);
          logMetric('env-network-downlink-mbps', {
            effective_type: effectiveType || 'unknown',
            downlink
          });
        }
        if (typeof rtt === 'number' && isFinite(rtt)) {
          this.tracker.histogram('env-network-rtt-ms', {
            effective_type: effectiveType || 'unknown'
          }).update(rtt);
          logMetric('env-network-rtt-ms', {
            effective_type: effectiveType || 'unknown',
            rtt
          });
        }
        if (typeof saveData === 'boolean') {
          this.tracker.counter('env-save-data-enabled-total', {
            enabled: saveData.toString()
          }).increment();
          logMetric('env-save-data-enabled-total', {
            enabled: saveData.toString()
          });
        }
      }
    } catch (_unused4) {
      // no-op
    }
  }

  /**
   * Initialize passive environment monitoring (records initial values and listens for network changes)
   */
  initEnvironmentMonitoring() {
    this.trackEnvironmentInfo();
    try {
      const nav = typeof navigator !== 'undefined' ? navigator : undefined;
      const connection = nav && (nav.connection || nav.mozConnection || nav.webkitConnection);
      if (connection && typeof connection.addEventListener === 'function') {
        this.networkChangeHandler = () => {
          this.tracker.counter('env-network-change-events-total').increment();
          this.trackEnvironmentInfo();
        };
        connection.addEventListener('change', this.networkChangeHandler);
      }
    } catch (_unused5) {
      // no-op
    }
  }

  /**
   * Stop environment monitoring (removes network change listener if added)
   */
  stopEnvironmentMonitoring() {
    try {
      const nav = typeof navigator !== 'undefined' ? navigator : undefined;
      const connection = nav && (nav.connection || nav.mozConnection || nav.webkitConnection);
      if (connection && typeof connection.removeEventListener === 'function' && this.networkChangeHandler) {
        connection.removeEventListener('change', this.networkChangeHandler);
        this.networkChangeHandler = undefined;
      }
    } catch (_unused6) {
      // no-op
    }
  }
}
let instance;
export const buildReactCompilerMetrics = tracker => {
  if (!instance) {
    instance = new ReactCompilerMetrics(tracker);
  }
  return instance;
};